// ============================================================================
// Suites (2026-10-06, docs/decisions.md "A suite is a workspace"): what the
// data model calls a workspace. One bucket of data (the `ws` label, unchanged), its
// apps and its own phone Home. A suite is installed from a manifest under a
// name the owner picks; the same manifest can be installed more than once.
//
// Pure and tested (suites.test.ts). The data model keeps the word workspace:
// a manifest becomes an ordinary Workspace create (with `spec.suite` = its id)
// plus that suite's Home in Settings `phones[slug]`.
// ============================================================================

import type { Hue } from '@/lib/colour'
import { ALL, CORE_NAV_KEYS, type CoreNavKey } from '@/kinds/workspace/schema'
import { navTicks } from '@/lib/nav'
import {
  defaultLayout,
  fitLayout,
  type HomeItem,
  type PhoneLayout,
  type WidgetKind,
  type WidgetSize,
} from '@/os/layout'

export type SuiteId = 'life' | 'devos' | 'blank'

export interface SuiteManifest {
  id: SuiteId
  title: string
  blurb: string
  /** one emoji */
  icon: string
  colour: Hue
  packs: string[]
  /** the core apps it shows; undefined is all of them (a pack may still hide some) */
  nav?: CoreNavKey[]
  /** its first Home, narrowed to `apps` (the app ids the new suite has) */
  home: (apps: readonly string[]) => PhoneLayout
  /**
   * What its day page is (Today's route, `/`): `life` the whole of Today (the
   * horizon, smoking, wellbeing, dates, the day's cards), `work` the code
   * day ("Now": what waits on you, today's tasks, review, what is going out),
   * `plain` the day's tasks and nothing about the owner's life.
   */
  day: SuiteDay
}

export type SuiteDay = 'life' | 'work' | 'plain'

/** The day page's name in the nav, on its tab and as its title. */
export const DAY_LABEL: Record<SuiteDay, string> = { life: 'Today', work: 'Now', plain: 'Today' }

const app = (id: string, k: string): HomeItem => ({ k, t: 'app', id })
const widget = (kind: WidgetKind, size: WidgetSize, k: string): HomeItem => ({ k, t: 'widget', kind, size })

/** Today and Inbox stay a tap away in every suite: they are where open tasks live. */
const DAY_DOCK = ['home', 'today', 'inbox']

const LIFE: SuiteManifest = {
  id: 'life',
  title: 'Life',
  blurb: 'Every app: the day, the calendar, notes, people, music, files and Prime',
  icon: '🌱',
  colour: 'sky',
  // Files (2026-10-31, docs/areas/files.md); a suite made before keeps its packs: tick Files in its editor
  packs: ['files'],
  home: (apps) => fitLayout(defaultLayout(), apps, DAY_DOCK),
  day: 'life',
}

// DevOS's core apps: all but the calendar and the weekly review, which the dev pack hides too
const DEV_HIDES = new Set<CoreNavKey>(['upcoming', 'calendar', 'review', 'someday', 'recurring'])

const DEVOS: SuiteManifest = {
  id: 'devos',
  title: 'DevOS',
  blurb: 'Code work first: the graph, Prime, the coding agents and releases',
  icon: '🛠️',
  colour: 'violet',
  packs: ['dev', 'learn', 'infra'],
  day: 'work',
  nav: CORE_NAV_KEYS.filter((k) => !DEV_HIDES.has(k)),
  home: (apps) =>
    fitLayout(
      {
        ...defaultLayout(),
        pages: [
          [
            widget('prime', 'wide', 'v1'),
            app('graph', 'v2'),
            app('prime', 'v3'),
            app('agents', 'v4'),
            app('/dev/releases', 'v5'),
            app('today', 'v10'),
            app('inbox', 'v11'),
            app('projects', 'v12'),
            app('pages', 'v13'),
          ],
          [
            widget('today', 'wide', 'v14'),
            widget('inbox', 'small', 'v15'),
            widget('focus', 'small', 'v16'),
            app('/learn', 'v17'),
            app('search', 'v19'),
            app('log', 'v20'),
            app('music', 'v21'),
            app('settings', 'v22'),
          ],
        ],
        // Home first, as everywhere; then straight into the Graph (the hub) and Prime
        dock: ['home', 'graph', 'prime'],
        wallpaper: 'lines',
      },
      apps,
      ['home', 'graph', 'prime', ...DAY_DOCK],
    ),
}

const BLANK: SuiteManifest = {
  id: 'blank',
  title: 'Blank',
  blurb: 'The essentials (Today, Inbox, Calendar, Review, Notes), to grow into something of its own',
  // a blank page to start on (a grey square read as a missing picture)
  icon: '🗒️',
  colour: 'slate',
  packs: [],
  day: 'plain',
  // the essentials: every open task must be on some page, so Calendar (dated, due) and
  // Review (Someday) stay; only the dev pack's recorded exception hides them
  nav: ['today', 'inbox', 'upcoming', 'review', 'pages'],
  home: (apps) =>
    fitLayout(
      {
        ...defaultLayout(),
        pages: [
          [
            widget('today', 'wide', 'b1'),
            widget('inbox', 'small', 'b2'),
            widget('clock', 'small', 'b3'),
            app('pages', 'b4'),
            app('settings', 'b5'),
          ],
        ],
        dock: DAY_DOCK,
        wallpaper: 'none',
      },
      apps,
      DAY_DOCK,
    ),
}

/** Every manifest, in the order the install sheet offers them. */
export const SUITES: SuiteManifest[] = [LIFE, DEVOS, BLANK]

export const suiteById = (id: string | undefined): SuiteManifest | undefined =>
  SUITES.find((s) => s.id === id)

/**
 * The manifest a workspace came from: the one it names, else inferred (a
 * workspace made before suites with the dev pack is DevOS, any other is Life).
 */
export const suiteOf = (w: { spec: { suite?: string; packs?: string[] } } | undefined): SuiteManifest =>
  suiteById(w?.spec.suite) ?? (w?.spec.packs?.includes('dev') ? DEVOS : LIFE)

/**
 * The day page of the suite the device is in. Everything is the whole day, so
 * it is Life's; so is a scope with no workspace yet.
 */
export const dayOf = (
  w: { spec: { suite?: string; packs?: string[] } } | undefined,
  scope: string,
): SuiteDay => (scope === ALL || !w ? 'life' : suiteOf(w).day)

/**
 * The `nav` to store for a manifest: none when it is what the packs give anyway
 * (so the pack's layout keeps following later changes, as the editor does),
 * else the manifest's list. `shown` are the core app keys, `hidden` what the
 * packs hide.
 */
export const navToStore = (
  m: Pick<SuiteManifest, 'nav'>,
  shown: readonly string[],
  hidden: readonly string[],
): CoreNavKey[] | undefined => {
  if (!m.nav) return undefined
  const want = new Set(m.nav.filter((k) => shown.includes(k)))
  const fallback = navTicks([...shown], undefined, [...hidden])
  const same = want.size === fallback.size && [...want].every((k) => fallback.has(k))
  return same ? undefined : m.nav
}

/** A workspace slug from words: lowercase letters, digits and dashes, at most 31. */
export const slugify = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 31)

/**
 * A slug for a new suite named `name` that no workspace (archived ones too: the
 * server refuses any slug already taken) has yet: `devos`, then `devos-2`, …
 */
export const uniqueSlug = (name: string, taken: Iterable<string>, fallback = 'suite'): string => {
  const used = new Set(taken)
  const base = slugify(name) || fallback
  if (!used.has(base)) return base
  for (let n = 2; ; n++) {
    const end = `-${n}`
    const slug = `${base.slice(0, 31 - end.length).replace(/-+$/, '')}${end}`
    if (!used.has(slug)) return slug
  }
}

/** The Workspace spec a manifest installs as, under `name` and `slug`. */
export const installSpec = (
  m: SuiteManifest,
  name: string,
  slug: string,
  o: { order: number; nav?: CoreNavKey[] },
) => ({
  name: name.trim() || m.title,
  slug,
  icon: m.icon,
  colour: m.colour,
  packs: [...m.packs],
  order: o.order,
  ...(o.nav ? { nav: o.nav } : {}),
  suite: m.id,
})
